import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ExButton,
  ButtonFlavor,
  ButtonType,
  ButtonSize,
  ExIcon,
  ExInput,
  SearchSize,
  SearchFlavor,
  IconVariant,
  ExSegmentedControls,
  ExSegmentedControl,
  ExTab,
  ExTabItem,
  AlertToastType,
  ToastController,
  ExEmptyState,
  TooltipPosition,
  TooltipAlignment,
} from '@boomi/exosphere';

import {
  ModelConfig,
  ModelConfigDetails,
  ModelsPageProps,
} from '../types/models.types';
import noResultsImage from '@assets/boomi-ai/ui-kit/agentFoundation/No_result.svg';
import ModelsTable from '../ModelsTable/ModelsTable';
import ModelsCardView from '../ModelsCardView/ModelsCardView';
import CreateModelConfigurationDrawer from '../CreateModelConfigurationDrawer/CreateModelConfigurationDrawer';
import { ProviderOption, RegisterModelFormData } from '../CreateModelConfigurationDrawer/types';
import ModelConfigDetailsDrawer from '../ModelConfigDetailsDrawer/ModelConfigDetailsDrawer';
import ModelsListDrawer from '../ModelsListDrawer/ModelsListDrawer';
import ModelConfigBlockDeleteDialog from '../ModelConfigBlockDeleteDialog/ModelConfigBlockDeleteDialog';
import ModelConfigDeleteConfirmationDialog from '../ModelConfigDeleteConfirmationDialog/ModelConfigDeleteConfirmationDialog';
import modelsApiService from '@services/agent-foundation/agent-foundation-models-api-service';
import { getEnvironments } from '@services/agent-foundation/agent-foundation-environments-api-service';
import './ModelsPage.scss';
import debounce from '@utils/general/general';
import { hasPrivilege } from '@utils/data/rbacService';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import UserPrivilege from '@utils/data/userPrivileges';
import BoomiModelsTable from '../BoomiModelsTable/BoomiModelsTable';
import ModelAgentsDrawer from '../ModelAgentsDrawer/ModelAgentsDrawer';
import FilterDropdown from '@components/FilterDropdown/FilterDropdown';
import { handleError } from '@services/agent-foundation/agent-foundation-api-service';
import TooltipWithLink from '@components/TooltipWithLink/TooltipWithLink';

function ModelsPage({ onRegisterModelClick }: Readonly<ModelsPageProps>) {
  const [t] = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const providerParam = searchParams.get('provider');
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedModels, setHasLoadedModels] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTableView, setIsTableView] = useState(true);
  const [showEmptyState, setShowEmptyState] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [isRegisterDrawerOpen, setIsRegisterDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'my-models' | 'boomi-managed'>('my-models');
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [selectedCardModelId, setSelectedCardModelId] = useState<string | null>(null);
  const [selectedModelName, setSelectedModelName] = useState<string>('');
  const [selectedModelProvider, setSelectedModelProvider] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingModel, setEditingModel] = useState<any>(null);
  const [showConfigDetailsDrawer, setShowConfigDetailsDrawer] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [showModelsListDrawer, setShowModelsListDrawer] = useState(false);
  const [selectedConfigName, setSelectedConfigName] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [isDrawerFromConfig, setIsDrawerFromConfig] = useState(false);
  const [showBlockDeleteDialog, setShowBlockDeleteDialog] = useState(false);
  const [selectedConfigForDelete, setSelectedConfigForDelete] = useState<
  ModelConfigDetails | null
  >(null);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [selectedConfigIdForDelete, setSelectedConfigIdForDelete] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>({});
  const [tableCurrentPage, setTableCurrentPage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(20);
  const [tableTotal, setTableTotal] = useState(0);
  const [cardCurrentPage, setCardCurrentPage] = useState(1);
  const [cardPageSize, setCardPageSize] = useState(15);
  const [cardTotal, setCardTotal] = useState(0);
  const [tableSortModel, setTableSortModel] = useState<{ sortBy?: string; sortOrder?: string }>({});

  const currentPage = isTableView ? tableCurrentPage : cardCurrentPage;
  const pageSize = isTableView ? tablePageSize : cardPageSize;
  const totalItems = isTableView ? tableTotal : cardTotal;
  const setCurrentPage = isTableView ? setTableCurrentPage : setCardCurrentPage;
  const setPageSize = isTableView ? setTablePageSize : setCardPageSize;
  const setTotalItems = isTableView ? setTableTotal : setCardTotal;
  const [environmentOptions, setEnvironmentOptions] = useState<
  Array<{ label: string; value: string; secondaryText?: string }>
  >([]);
  const [isLoadingEnvironments, setIsLoadingEnvironments] = useState(true);
  const {
    isMobileView, userPrivileges,
  } = useBoomiAgentFoundationContext();
  useEffect(() => {
    const loadEnvironmentOptions = async () => {
      setIsLoadingEnvironments(true);
      try {
        const response: any = await getEnvironments({
          classification: 'PRODUCTION',
          has_common_runtime: true,
          has_model_configurations: true,
        });
        const environments = response?.data?.items || response?.data || response || [];
        const options = (Array.isArray(environments) ? environments : []).map((env: any) => ({
          label: env.name || env.environment_name,
          value: env.ag_environment_id || env.integration_environment_id,
          secondaryText: env.runtimes?.[0]?.runtime_name,
        }));
        setEnvironmentOptions(options);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load environments:', error);
        setEnvironmentOptions([]);
      } finally {
        setIsLoadingEnvironments(false);
      }
    };
    loadEnvironmentOptions();
  }, []);

  useEffect(() => {
    if (!providerParam) {
      return;
    }
    setIsRegisterDrawerOpen(false);
    const formattedProvider = providerParam.charAt(0).toUpperCase() + providerParam.slice(1);
    setSelectedModelProvider(formattedProvider);
    setIsRegisterDrawerOpen(true);
    setSearchParams({}, { replace: true });
  }, [providerParam, setSearchParams]);

  const PROVIDER_OPTIONS: ProviderOption[] = [
    { label: 'Any', value: 'any', icon: '' },
    { value: 'anthropic', label: t('models_provider_anthropic'), icon: 'Anthropic' },
    { value: 'openai', label: t('models_provider_openai'), icon: 'OpenAI' },
    { value: 'google', label: t('models_provider_google'), icon: 'Gemini' },
    // { value: 'Custom', label: 'Custom' },
  ];

  // Filtering is now done server-side via API
  const filteredModels = useMemo(() => models, [models]);

  useEffect(() => {
    if (showEmptyState && filteredModels.length > 0) {
      setShowEmptyState(false);
    }
  }, [showEmptyState, filteredModels]);

  useEffect(() => {
    if (!showEmptyState && !showControls && !isLoading && !isLoadingEnvironments) {
      setShowControls(true);
    }
  }, [showEmptyState, showControls, isLoading, isLoadingEnvironments]);

  const loadModels = useCallback(async (
    page: number = 1,
    size: number = 10,
    sortBy?: string,
    sortOrder?: string,
  ) => {
    setIsLoading(true);
    try {
      // Build API filters with search term, provider, and pagination
      const apiFilters: Record<string, any> = {
        page,
        page_size: size,
      };

      if (searchTerm) {
        apiFilters.search_term = searchTerm;
      }

      // Format provider filter (convert array to single value if needed)
      if (selectedFilters.provider && selectedFilters.provider !== 'any') {
        apiFilters.provider = selectedFilters.provider;
      }
      if (selectedFilters.environment) {
        apiFilters.environment_id = selectedFilters.environment;
      }

      if (sortBy) {
        apiFilters.sort_by = sortBy;
        apiFilters.sort_order = sortOrder;
      }

      const response = await modelsApiService.getModelConfigs(apiFilters);
      const items = response?.items || [];
      const total = response?.total_count ?? 0;
      setModels(items);
      setHasLoadedModels(true);
      if (isTableView) {
        setTableTotal(total);
      } else {
        setCardTotal(total);
      }
      if (total > 0) {
        setShowEmptyState(false);
      } else {
        setShowEmptyState(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load models:', error);
      setModels([]);
      if (isTableView) {
        setTableTotal(0);
      } else {
        setCardTotal(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, selectedFilters, isTableView]);

  useEffect(() => {
    if (isTableView) {
      setTableCurrentPage(1);
      loadModels(1, tablePageSize);
    } else {
      setCardCurrentPage(1);
      loadModels(1, cardPageSize);
    }
  }, [selectedFilters, searchTerm, isTableView, loadModels,
    tablePageSize, cardPageSize]);

  useEffect(() => {
    if (isTableView && tableSortModel.sortBy) {
      setTableCurrentPage(1);
      loadModels(1, tablePageSize, tableSortModel.sortBy, tableSortModel.sortOrder);
    }
  }, [tableSortModel.sortBy, tableSortModel.sortOrder, isTableView,
    tablePageSize, loadModels]);

  const handleModelSelect = (model: ModelConfig) => {
    const configId = model.id || model.model_id;
    if (!configId) return;
    setSelectedCardModelId(configId);
    setSelectedConfigId(configId);
    setShowConfigDetailsDrawer(true);
  };

  const handleEditModelConfiguration = (model: ModelConfig) => {
    setSelectedCardModelId(model.id || model.model_id || null);
    setEditingModel(model);
    setIsEditMode(true);
    setIsRegisterDrawerOpen(true);
  };

  const handleDeleteModelConfiguration = (modelId: string) => {
    setSelectedConfigIdForDelete(modelId);
    setShowDeleteConfirmationDialog(true);
  };

  const handleDeleteDialogClose = useCallback(() => {
    setShowDeleteConfirmationDialog(false);
    setSelectedConfigIdForDelete('');
  }, []);

  const handleDeleteBlocked = useCallback((modelConfig: ModelConfigDetails) => {
    setShowDeleteConfirmationDialog(false);
    setSelectedConfigIdForDelete('');
    setSelectedConfigForDelete(modelConfig);
    setShowBlockDeleteDialog(true);
  }, []);

  const handleDeleteConfirmed = async () => {
    setCurrentPage(1);
    try {
      // Reload models from the first page to ensure consistency
      const response = await modelsApiService.getModelConfigs({
        ...selectedFilters,
        page: 1,
        page_size: pageSize,
      });
      const items = response?.items || [];
      const total = response?.total_count ?? 0;
      setModels(items);
      setTotalItems(total);

      // Update empty state based on loaded models
      if (items.length === 0) {
        setShowEmptyState(true);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('models_error_delete_failed');
      ToastController.show({
        description: errorMessage,
        type: AlertToastType.ERROR,
      });
    }
  };

  const [selectedConfigIdForModels, setSelectedConfigIdForModels] = useState<string>('');

  const handleViewModels = (modelId: string, modelName: string, provider: string) => {
    setSelectedCardModelId(modelId);
    setSelectedConfigName(modelName);
    setSelectedProvider(provider);
    setSelectedConfigIdForModels(modelId);
    setIsDrawerFromConfig(false);
    setShowModelsListDrawer(true);
  };

  const handleShowAgents = (model: any) => {
    setShowConfigDetailsDrawer(true);
    setSelectedModelId(model.id);
    setSelectedModelName(model.name);
    setSelectedModelProvider(model.provider || t('custom'));
  };

  const handleRegisterModel = async (formData: RegisterModelFormData) => {
    const successMessage = isEditMode
      ? t('models_success_updated')
      : t('models_success_registered');

    if (isEditMode && editingModel) {
      const id = editingModel.id || '';
      const response = await modelsApiService.updateModelConfiguration(
        id,
        formData,
      );
      if (!response.success) {
        handleError(response, t('models_error_update_failed'));
        return { success: false, error: response.error, message: response.message };
      }
    } else {
      const res = await modelsApiService.registerModelConfiguration(
        formData,
      );
      if (!res.success) {
        handleError(res, t('models_error_register_failed'));
        return { success: false, error: res.error, message: res.message };
      }
    }

    // Close the drawer before showing the success toast so the toast is visible
    // after the drawer transition has started.
    setIsRegisterDrawerOpen(false);
    setIsEditMode(false);
    setEditingModel(null);
    ToastController.show({
      description: successMessage,
      type: AlertToastType.SUCCESS,
    });

    // Refresh the models list from the first page
    setCurrentPage(1);
    await loadModels(1, pageSize);

    return { success: true };
  };

  const handleSearchInput = useCallback(debounce((e: any) => {
    setSearchTerm(e.target.value);
  }, 500), []);

  const handleOpenRegisterDrawer = () => {
    setIsRegisterDrawerOpen(true);
    onRegisterModelClick?.();
  };

  const hasAgentEditPermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_EDIT);
  const hasAgentCreatePermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE);
  const isAnyFilterActive = useMemo(() => {
    const hasEnvFilter = (selectedFilters.environment ?? []).length > 0;
    const hasProviderFilter = selectedFilters.provider && selectedFilters.provider !== 'any';
    return hasEnvFilter || hasProviderFilter;
  }, [selectedFilters]);
  return (
    <div className="page-models-container">
      <div className="models-page">
        <div className="models-header">
          {t('models_page_title')}
          <TooltipWithLink
            position={isMobileView ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
            alignment={isMobileView ? TooltipAlignment.START : TooltipAlignment.MIDDLE}
            infoTextKey="models_page_tooltip"
            linkTextKey="model_page_tooltip_link_text"
            testId="models-page-help-link"
            iconButtonTestId="models-page-help-tooltip-trigger"
          />
        </div>
        <div className="models-sub-header">
          {t('models_page_description')}
        </div>
        {hasAgentEditPermission && hasAgentCreatePermission && (
          <div className="models-btn">
            <ExButton
              type={ButtonType.PRIMARY}
              size={ButtonSize.DEFAULT}
              flavor={ButtonFlavor.BASE}
              className="create-tools-btn"
              onClick={handleOpenRegisterDrawer}
              data-testid="register-new-model-btn"
            >
              <ExIcon
                icon="Add new"
                hide-browser-tooltip=""
                className="button-icon"
                slot="prefix"
              />
              {t('models_register_button')}
            </ExButton>
          </div>
        )}

        <ExTab role="tablist" className="models-page__tabs">
          <ExTabItem
            selected={activeTab === 'my-models'}
            onClick={() => setActiveTab('my-models')}
            data-testid="models-my-models-tab"
          >
            {t('models_my_models_tab')}
          </ExTabItem>
          <ExTabItem
            selected={activeTab === 'boomi-managed'}
            onClick={() => setActiveTab('boomi-managed')}
            data-testid="models-boomi-managed-tab"
          >
            {t('models_boomi_managed_tab')}
          </ExTabItem>
        </ExTab>

        {activeTab === 'my-models' && (
          <>
            {showControls && (
              <div className="models-page__toolbar">
                <div className="models-page__search-wrapper">
                  <ExInput
                    className="models-page__search"
                    placeholder={t('models_page_search_placeholder')}
                    data-testid="model-search-filter-input"
                    type="text"
                    clearable
                    size={SearchSize.MEDIUM}
                    flavor={SearchFlavor.WHITE}
                    onInput={handleSearchInput}
                  >
                    <ExIcon icon="Search" variant={IconVariant.ICON} slot="prefix" />
                  </ExInput>
                </div>
                <div>
                  <FilterDropdown
                    combobox={{
                      key: 'environment',
                      title: t('models_card_environment'),
                      placeholder: t('filter_select_environment', 'Select Environment'),
                      options: environmentOptions,
                    }}
                    filterPills={[
                      {
                        label: t('provider'),
                        key: 'provider',
                        options: PROVIDER_OPTIONS,
                      },
                    ]}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    isAnyFilterActive={isAnyFilterActive}
                    testIdPrefix="models-list"
                  />
                </div>
                <div className="models-page__view-switcher">
                  <ExSegmentedControls data-testid="models-view-switcher">
                    <ExSegmentedControl
                      tooltiptext={t('models_view_card_tooltip')}
                      icon="Grid view"
                      selected={!isTableView}
                      onClick={() => {
                        if (isTableView) { setShowEmptyState(true); }
                        setIsTableView(false);
                      }}
                      data-testid="models-card-view"
                    />
                    <ExSegmentedControl
                      tooltiptext={t('models_view_table_tooltip')}
                      icon="Table"
                      selected={isTableView}
                      onClick={() => {
                        if (!isTableView) { setShowEmptyState(true); }
                        setIsTableView(true);
                      }}
                      data-testid="models-table-view"
                    />
                  </ExSegmentedControls>
                </div>
              </div>
            )}

            {filteredModels.length === 0 && models.length > 0 && !showEmptyState && (
              <div className="models-page__no-results">
                <ExEmptyState
                  label={t('models_no_results_title')}
                  text={t('models_no_results_text')}
                  data-testid="models-no-results-state"
                >
                  <div slot="header" data-testid="models-no-results-header">
                    <img
                      alt={t('models_no_results_alt')}
                      src={noResultsImage}
                      data-testid="models-no-results-image"
                    />
                  </div>
                </ExEmptyState>
              </div>
            )}

            {!(filteredModels.length === 0 && models.length > 0 && !showEmptyState) && (
              isTableView ? (
                <ModelsTable
                  models={filteredModels}
                  isLoading={isLoading || isLoadingEnvironments}
                  isInitialLoading={!hasLoadedModels || isLoadingEnvironments}
                  onModelSelect={handleModelSelect}
                  onEdit={handleEditModelConfiguration}
                  onDelete={handleDeleteModelConfiguration}
                  onViewModels={handleViewModels}
                  showEmptyState={showEmptyState}
                  setShowEmptyState={setShowEmptyState}
                  searchTerm={searchTerm}
                  isAnyFilterActive={isAnyFilterActive}
                  setModels={setModels}
                  setIsLoading={setIsLoading}
                  hasEditPermission={hasAgentEditPermission}
                  hasCreatePermission={hasAgentCreatePermission}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={totalItems}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    loadModels(page, pageSize);
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setCurrentPage(1);
                  }}
                  onSortChange={(sortBy?: string, sortOrder?: string) => {
                    setTableCurrentPage(1);
                    setTableSortModel({
                      sortBy,
                      sortOrder,
                    });
                  }}
                />
              ) : (
                <ModelsCardView
                  models={filteredModels}
                  isLoading={isLoading || isLoadingEnvironments}
                  isInitialLoading={!hasLoadedModels || isLoadingEnvironments}
                  onModelSelect={handleModelSelect}
                  selectedModelId={selectedCardModelId ?? ''}
                  onEdit={handleEditModelConfiguration}
                  onDelete={handleDeleteModelConfiguration}
                  onViewModels={handleViewModels}
                  showEmptyState={showEmptyState}
                  setShowEmptyState={setShowEmptyState}
                  searchTerm={searchTerm}
                  isAnyFilterActive={isAnyFilterActive}
                  setModels={setModels}
                  setIsLoading={setIsLoading}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={totalItems}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    loadModels(page, pageSize);
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setCurrentPage(1);
                  }}
                  hasEditPermission={hasAgentEditPermission}
                  hasCreatePermission={hasAgentCreatePermission}
                />
              )
            )}
          </>
        )}

        {activeTab === 'boomi-managed' && (
          <BoomiModelsTable />
        )}
      </div>
      {isRegisterDrawerOpen && (
        <CreateModelConfigurationDrawer
          isOpen={isRegisterDrawerOpen}
          onClose={() => {
            setIsRegisterDrawerOpen(false);
            setIsEditMode(false);
            setEditingModel(null);
            setSelectedModelProvider('');
            setSelectedCardModelId(null);
          }}
          onRegister={handleRegisterModel}
          testId="models-page-register-drawer"
          isEditMode={isEditMode}
          editingModel={editingModel}
          preselectedProvider={selectedModelProvider}
        />
      )}
      {showConfigDetailsDrawer && selectedConfigId && (
        <ModelConfigDetailsDrawer
          open={showConfigDetailsDrawer}
          configId={selectedConfigId}
          onClose={() => {
            setShowConfigDetailsDrawer(false);
            setSelectedConfigId(null);
            setSelectedCardModelId(null);
          }}
          onEdit={() => {
            setIsEditMode(true);
            setEditingModel({ id: selectedConfigId } as ModelConfig);
            setIsRegisterDrawerOpen(true);
            setShowConfigDetailsDrawer(false);
          }}
          // onModelsClick={() => {
          //   setShowConfigDetailsDrawer(false);
          //   setSelectedConfig(selectedConfig);
          // }}
          // onDeployedAgentsClick={() => {
          //   setSelectedModelId(selectedConfig.id);
          //   setSelectedModelName(selectedConfig.name);
          //   setSelectedModelProvider(selectedConfig.provider);
          //   setShowConfigDetailsDrawer(false);
          //   setSelectedConfig(selectedConfig);
          // }}
        />
      )}
      {selectedModelId && (
        <ModelAgentsDrawer
          open={!!selectedModelId}
          modelId={selectedModelId}
          modelName={selectedModelName}
          modelConfigId=""
          provider={selectedModelProvider}
          onClose={() => {
            setSelectedModelId(null);
            setSelectedModelName('');
            setSelectedModelProvider('Boomi');
            setSelectedCardModelId(null);
          }}
        />
      )}
      {showModelsListDrawer && (
        <ModelsListDrawer
          open={showModelsListDrawer}
          configName={selectedConfigName}
          configId={selectedConfigIdForModels}
          provider={selectedProvider}
          navigationEnabled={isDrawerFromConfig}
          onClose={() => {
            setShowModelsListDrawer(false);
            setSelectedConfigName('');
            setSelectedConfigIdForModels('');
            setSelectedProvider('');
            setIsDrawerFromConfig(false);
            setSelectedCardModelId(null);
          }}
          onShowAgents={handleShowAgents}
        />
      )}
      {showBlockDeleteDialog && selectedConfigForDelete && (
        <ModelConfigBlockDeleteDialog
          open={showBlockDeleteDialog}
          agentCount={selectedConfigForDelete.deployment_count || 0}
          onClose={() => {
            setShowBlockDeleteDialog(false);
            setSelectedConfigForDelete(null);
          }}
        />
      )}
      {showDeleteConfirmationDialog && selectedConfigIdForDelete && (
        <ModelConfigDeleteConfirmationDialog
          open={showDeleteConfirmationDialog}
          configId={selectedConfigIdForDelete}
          onClose={handleDeleteDialogClose}
          onDeleteBlocked={handleDeleteBlocked}
          onDeleteSuccess={handleDeleteConfirmed}
        />
      )}
    </div>
  );
}

export default ModelsPage;
